import { ReactElement, useState } from "react"

import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { ConfirmModal } from "../../../../../shared/components/dialogs/ConfirmModal"
import { LockedDialog, useLockedDialogState } from "../../../../../shared/components/dialogs/LockedDialog"
import { StandardSuccessDialog } from "../../../../../shared/components/dialogs/StandardSuccessDialog"
import {
    StandardErrorDialog,
    useStandardErrorDialogState,
} from "../../../../../shared/components/dialogs/StandardErrorDialog"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { ApiError, ErrorDisplay } from "../../../../../core/api/errorTypes"
import { refundOrder } from "../../../../receipt/ordersApi"
import { Order } from "../../../controlKitchenTypes"
import { AccountingQuery } from "../accountingApi"

type UseOrderRefundFlowReturn = Readonly<{
    requestRefund: (order: Order) => void
    dialogs: ReactElement
}>

function isAlreadyRefunded(order: Order): boolean {
    return (order.creditNoteDetails?.creditNoteOrderIds?.length ?? 0) > 0
}

export function useOrderRefundFlow(): UseOrderRefundFlowReturn {
    const { l10n } = useAppLocalization()
    const [targetOrder, setTargetOrder] = useState<Order | undefined>(undefined)
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const { errorDialog, openErrorDialog, closeErrorDialog } = useStandardErrorDialogState()
    const { lockedDialog, openLockedDialog, closeLockedDialog } = useLockedDialogState()

    const { mutate: refund, isLoading: isRefunding } = useApiMutation(refundOrder, {
        errorDisplay: ErrorDisplay.NONE,
        onSuccess: async (_params, _data, { queryClient }) => {
            setShowConfirmModal(false)
            setShowSuccessModal(true)
            await queryClient.invalidateQueries({ queryKey: [AccountingQuery.ORDERS_TRANSACTIONS_LIST] })
        },
        onError: async (_params, error) => {
            setShowConfirmModal(false)
            const displayMessage = error instanceof ApiError && error.hasApiMessage ? error.displayMessage : undefined
            openErrorDialog({
                title: l10n.getString("order-accounting-refund-pos-order-title"),
                description: displayMessage || l10n.getString("transactions-table-list-refund-error-message"),
            })
        },
    })

    const requestRefund = (order: Order) => {
        if (isAlreadyRefunded(order)) {
            openLockedDialog({
                title: l10n.getString("order-accounting-refund-pos-order-already-refunded-title"),
                body: l10n.getString("order-accounting-refund-pos-order-already-refunded-body"),
            })
            return
        }

        const refundAction = order.orderAction?.refundPOSOrder
        if (refundAction?.allow) {
            setTargetOrder(order)
            setShowConfirmModal(true)
        } else {
            openLockedDialog({
                title:
                    refundAction?.message?.title ||
                    l10n.getString("order-accounting-refund-pos-order-locked-dialog-title"),
                body:
                    refundAction?.message?.body ||
                    l10n.getString("order-accounting-refund-pos-order-locked-dialog-body"),
            })
        }
    }

    const handleConfirmRefund = () => {
        if (!targetOrder) return

        refund({ orderId: targetOrder.id })
    }

    const dialogs = (
        <>
            {targetOrder && (
                <LocalizedStrict
                    id="order-accounting-refund-pos-order-confirm-dialog"
                    attrs={{ title: true, message: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
                    vars={{ orderId: targetOrder.id.toString() }}
                >
                    <ConfirmModal
                        open={showConfirmModal}
                        onClose={() => setShowConfirmModal(false)}
                        title="Refund"
                        message="Are you sure you want to refund order?"
                        handleClick={handleConfirmRefund}
                        buttonLabelSubmit="Confirm"
                        buttonLabelCancel="Cancel"
                        buttonSubmitDisabled={isRefunding}
                        buttonSubmitLoading={isRefunding}
                        autoCloseAfterSubmit={false}
                    />
                </LocalizedStrict>
            )}

            {showSuccessModal && (
                <StandardSuccessDialog
                    open={showSuccessModal}
                    dialogType="refundOrder"
                    onClose={() => setShowSuccessModal(false)}
                />
            )}

            {errorDialog.open && <StandardErrorDialog dialogDetails={errorDialog} onClose={closeErrorDialog} />}

            {lockedDialog.open && <LockedDialog dialogDetails={lockedDialog} onClose={closeLockedDialog} />}
        </>
    )

    return { requestRefund, dialogs }
}
